import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useNgoPortalQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { useMemo } from 'react';
import { AnimatedList, useAnimatedList } from '@worldcoo/ui/custom/animated-list';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import { Input } from '@worldcoo/ui/components/input';
import { cn } from '@worldcoo/ui/lib/utils';
import { useAppForm } from '@/lib/use-app-form';
import { FormReadOnlyProvider, withForm } from '@worldcoo/ui/components/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DonationAmountsCard } from './-components/donation-amounts-card';
import { EditFooter } from './-components/edit-footer';
import { useRegisterFormHandle } from './-components/form-context';

import { AiTranslatedHint } from './-components/ai-translated-hint';
import { LanguageDropdown } from './-components/language-dropdown';
import { showTranslationToast, showTranslationErrorToast } from './-components/translation-toast';
import { Button } from '@worldcoo/ui/components/button';
import { generateCaptchaToken } from '@/lib/captcha';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import type { AiTranslatedMeta } from '@worldcoo/monolith/core';
import { fillTranslatable, collectTranslatableEntries, autoTranslateMissingTranslations } from '@/lib/locale-utils';
import { uploadPortalFiles } from '@/lib/upload-to-s3';
import { portalFormOpts, type SliderItemForm, type FaqItemForm } from './-components/portal-form.opts';
import { useUploadTexts } from './-components/use-upload-texts';
import { buildPortalUrl, getPortalDomainSuffix } from '@/lib/shared';
import { portalFormSchema } from '@/lib/project-schemas';
import { EDIT_MODE_TOGGLE_ENABLED } from '@/lib/feature-flags';

export const Route = createFileRoute('/ngos/portals/$portalId/')({
	component: RouteComponent,
});

/** Build a per-field Record<lang, value> from textsCollection, pre-filling all languages with '' */
function buildTextsMap(
	textsCollection: { langCode: string; [key: string]: unknown }[],
	field: string,
	languages: string[],
): Record<string, string> {
	const map: Record<string, string> = { translatable: '' };
	for (const lang of languages) map[lang] = '';
	for (const entry of textsCollection) {
		const value = entry[field];
		map[entry.langCode] = typeof value === 'string' ? value : '';
	}
	return map;
}

/** Build FAQ map: Record<lang, FaqItemForm[]>, pre-filling all languages with [] */
function buildFaqMap(
	textsCollection: { langCode: string; faqItems?: { question: string; answer: string }[] }[],
	languages: string[],
): Record<string, FaqItemForm[]> {
	const map: Record<string, FaqItemForm[]> = {};
	for (const lang of languages) map[lang] = [];
	for (const entry of textsCollection) {
		map[entry.langCode] = (entry.faqItems ?? []).map((f) => ({
			_key: crypto.randomUUID(),
			question: f.question,
			answer: f.answer,
		}));
	}
	return map;
}

/** Build slider defaults from server data, pre-filling all languages */
function buildSliderDefaults(
	sliderItems: {
		image?: { location: string; width: number; height: number };
		texts: { langCode: string; quote: string; authorName: string; imageAlt?: string }[];
	}[],
	languages: string[],
): SliderItemForm[] {
	return sliderItems.map((item) => {
		const texts: Record<string, { quote: string; authorName: string; imageAlt: string }> = {};
		for (const lang of languages) texts[lang] = { quote: '', authorName: '', imageAlt: '' };
		for (const t of item.texts)
			texts[t.langCode] = { quote: t.quote, authorName: t.authorName, imageAlt: t.imageAlt ?? '' };
		return {
			_key: crypto.randomUUID(),
			existingImageUrl: item.image?.location,
			image: undefined as File | '' | undefined,
			texts,
		};
	});
}

function RouteComponent() {
	const { t } = useLingui();
	const { portalId } = Route.useParams();
	const { editing, plang } = useSearch({ from: '/ngos/portals/$portalId' });
	const isEditing = !EDIT_MODE_TOGGLE_ENABLED || editing === true;
	const navigate = useNavigate({ from: '/ngos/portals/$portalId' });
	const portal = useNgoPortalQuery(portalId);
	const queryClient = useQueryClient();
	const mutation = useMutation(trpc.ngo.updatePortal.mutationOptions());
	const translateMutation = useMutation(trpc.translate.translateProjectTexts.mutationOptions());
	const { tab } = useSearch({ from: '/ngos/portals/$portalId' });
	const activeTab = tab ?? 'languages';

	const defaultLang = portal.languages[0] ?? 'SPA';

	const langs = portal.languages;

	const defaultAmounts = useMemo(() => {
		const defaults = [10, 50, 100, 150];
		const saved = portal.amountOptions.map((o) => o['EUR'] ?? 0);
		// Pad up to 4 amounts with defaults so the form always shows 4 slots
		const amounts = saved.length >= defaults.length ? saved : [...saved, ...defaults.slice(saved.length)];
		// Build equivalent texts from textsCollection (per-language amountEquivalents arrays)
		const equivalents: Record<string, string>[] = [];
		for (const tc of portal.textsCollection) {
			if (tc.amountEquivalents) {
				for (let i = 0; i < tc.amountEquivalents.length; i++) {
					const text = tc.amountEquivalents[i];
					if (text) {
						const entry = (equivalents[i] = equivalents[i] ?? {});
						entry[tc.langCode] = text;
					}
				}
			}
		}
		return amounts.map((a, i) => ({ _id: `da-${i}`, amount: a, equivalent: fillTranslatable(equivalents[i], langs) }));
	}, [portal.amountOptions, portal.textsCollection, langs]);

	const defaultFaqItems = useMemo(() => buildFaqMap(portal.textsCollection, langs), [portal.textsCollection, langs]);
	const defaultSliderItems = useMemo(() => buildSliderDefaults(portal.sliderItems, langs), [portal.sliderItems, langs]);

	const validationLang = plang ?? defaultLang;

	const form = useAppForm({
		...portalFormOpts,
		validators: { onSubmit: portalFormSchema(validationLang) },
		defaultValues: {
			title: buildTextsMap(portal.textsCollection, 'title', langs),
			presentation: buildTextsMap(portal.textsCollection, 'presentation', langs),
			imageAlt: buildTextsMap(portal.textsCollection, 'imageAlt', langs),
			subdomain: portalId,
			donationAmounts: defaultAmounts,
			themeBaseColor: portal.themeBaseColor ?? '#f28e41',
			themeSecondaryColor: portal.themeSecondaryColor ?? '#1a1a1a',
			logo: undefined as File | '' | undefined,
			coverImage: undefined as File | '' | undefined,
			languages: [...portal.languages],
			defaultLanguage: defaultLang,
			aiTranslated: {} as AiTranslatedMeta,
			faqItems: defaultFaqItems,
			sliderItems: defaultSliderItems,
		},
		onSubmit: async ({ value }) => {
			const dLang = value.defaultLanguage;

			const {
				values: translatedValues,
				translatedLanguages,
				success,
			} = await autoTranslateMissingTranslations({
				values: value,
				defaultLanguage: dLang,
				languages: value.languages,
				translate: async (input) =>
					translateMutation.mutateAsync({ ...input, captchaToken: await generateCaptchaToken() }),
			});
			if (translatedLanguages.length > 0) {
				if (success) showTranslationToast(dLang, translatedLanguages);
				else showTranslationErrorToast();
			}

			// Upload portal files to S3 via presigned URLs
			const sliderFilesToUpload: { index: number; file: File }[] = [];
			for (let i = 0; i < translatedValues.sliderItems.length; i++) {
				const img = translatedValues.sliderItems[i]!.image;
				if (img instanceof File) sliderFilesToUpload.push({ index: i, file: img });
			}

			const uploaded = await uploadPortalFiles(
				{
					coverImage: translatedValues.coverImage,
					logo: translatedValues.logo,
					sliderImages: sliderFilesToUpload,
				},
				portalId,
			);

			// Build textsCollection from per-field language maps
			const textsCollection = translatedValues.languages.map((langCode) => {
				const equivalents = translatedValues.donationAmounts.map((d) => d.equivalent[langCode] || '');
				const hasEquivalents = equivalents.some((e) => e.length > 0);
				return {
					langCode,
					title: translatedValues.title[langCode] || undefined,
					presentation: translatedValues.presentation[langCode] || undefined,
					imageAlt: translatedValues.imageAlt[langCode] || undefined,
					faqItems: translatedValues.faqItems[langCode]?.filter((f) => f.question || f.answer),
					amountEquivalents: hasEquivalents ? equivalents : undefined,
				};
			});

			// Build slider items (texts only, images sent separately as S3 keys)
			const sliderItems = translatedValues.sliderItems.map((item) => ({
				texts: translatedValues.languages
					.filter((langCode) => item.texts[langCode])
					.map((langCode) => ({
						langCode,
						quote: item.texts[langCode]!.quote,
						authorName: item.texts[langCode]!.authorName,
						imageAlt: item.texts[langCode]!.imageAlt || undefined,
					})),
			}));

			// Map uploaded slider image keys
			const sliderImages = uploaded.sliderImages.map(({ index, key }) => ({ index, image: key }));

			// Reorder languages so defaultLanguage is first
			const orderedLanguages = [
				translatedValues.defaultLanguage,
				...translatedValues.languages.filter((l) => l !== translatedValues.defaultLanguage),
			];

			const input: Parameters<typeof mutation.mutateAsync>[0] = {
				portalId,
				languages: orderedLanguages,
				themeBaseColor: translatedValues.themeBaseColor,
				themeSecondaryColor: translatedValues.themeSecondaryColor,
				amountOptions: translatedValues.donationAmounts.filter((d) => d.amount > 0).map((d) => ({ EUR: d.amount })),
				textsCollection,
				sliderItems: sliderItems.length > 0 ? sliderItems : undefined,
				sliderImages: sliderImages.length > 0 ? sliderImages : undefined,
			};

			if (uploaded.logo) input.logoImage = uploaded.logo;
			else if (translatedValues.logo === '') input.logoImage = '';
			if (uploaded.coverImage) input.coverImage = uploaded.coverImage;
			else if (translatedValues.coverImage === '') input.coverImage = '';

			await mutation.mutateAsync(input);
			const savedLanguages = form.state.values.languages as string[];
			const keepPlang = plang && savedLanguages.includes(plang) ? plang : undefined;

			await Promise.all([
				queryClient.invalidateQueries({ queryKey: trpc.ngo.getPortal.queryKey({ portalId }) }),
				queryClient.invalidateQueries({ queryKey: trpc.ngo.getPortals.queryKey() }),
			]);

			// Reset form so file fields become undefined and image previews
			// switch from stale blob URLs to the new server URLs from the re-fetched query.
			// We need to use a timeout because of this issue: https://github.com/TanStack/form/issues/1681
			setTimeout(() => {
				form.reset();
				navigate({ search: (prev) => ({ ...prev, editing: undefined, plang: keepPlang }) });
			});
		},
	});

	useRegisterFormHandle(form);
	const uploadTexts = useUploadTexts();
	const selectedLang = plang ?? (isEditing ? (form.getFieldValue('defaultLanguage') as string) : defaultLang);
	const activeLanguages = isEditing ? (form.getFieldValue('languages') as string[]) : portal.languages;
	const activeDefaultLang = isEditing ? (form.getFieldValue('defaultLanguage') as string) : defaultLang;
	const changeLang = (langCode: string | undefined) => {
		navigate({
			search: (prev) => ({
				...prev,
				plang: langCode === activeDefaultLang ? undefined : langCode,
			}),
		});
	};
	const languageDropdown = (
		<LanguageDropdown
			languages={activeLanguages}
			selectedLang={selectedLang}
			defaultLanguage={activeDefaultLang}
			onLanguageChange={changeLang}
			size="sm"
		/>
	);

	const approveTranslation = (fieldName: string) => {
		const current = form.getFieldValue('aiTranslated');
		const langs = current[fieldName];
		if (!langs) return;
		const next = langs.filter((l: string) => l !== selectedLang);
		if (next.length > 0) {
			form.setFieldValue('aiTranslated', { ...current, [fieldName]: next });
		} else {
			const { [fieldName]: _, ...rest } = current;
			form.setFieldValue('aiTranslated', rest);
		}
	};

	const autoApproveListeners = (fieldName: string) =>
		isEditing ? { onChange: () => approveTranslation(fieldName) } : undefined;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<FormReadOnlyProvider value={!isEditing}>
			<form
				id="portal-edit-form"
				onSubmit={handleSubmit}
				className="flex w-full max-w-[600px] flex-col gap-6 px-8 py-6"
			>
				{activeTab === 'details' && (
					<>
						<Card className="shadow-warm">
							<CardHeader className="flex items-center justify-between">
								<CardTitle className="text-xl">{t`Details`}</CardTitle>
								{languageDropdown}
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<form.Subscribe
									selector={(state: { values: { aiTranslated: AiTranslatedMeta; defaultLanguage: string } }) => ({
										aiTranslated: state.values.aiTranslated,
										defaultLanguage: state.values.defaultLanguage,
									})}
								>
									{({ aiTranslated, defaultLanguage }: { aiTranslated: AiTranslatedMeta; defaultLanguage: string }) => (
										<>
											<div className="flex flex-col gap-2">
												<form.AppField
													name={`title.${selectedLang}`}
													listeners={autoApproveListeners('title')}
													children={(field) => (
														<field.TextField
															label={t`Portal name`}
															description={t`The general title that will be displayed prominently on the portal where the different projects eligible to receive funding can be seen.`}
														/>
													)}
												/>
												<AiTranslatedHint
													fieldName="title"
													selectedLang={selectedLang}
													defaultLanguage={defaultLanguage}
													aiTranslated={aiTranslated}
													onApprove={isEditing ? () => approveTranslation('title') : undefined}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<form.AppField
													name={`presentation.${selectedLang}`}
													listeners={autoApproveListeners('presentation')}
													children={(field) => (
														<field.SimpleEditorField
															label={t`Presentation`}
															description={t`A brief text that will be displayed prominently on the portal, below the portal name, so that whoever enters knows what it is about, what kind of project they will find, what they can do on this portal, etc.`}
														/>
													)}
												/>
												<AiTranslatedHint
													fieldName="presentation"
													selectedLang={selectedLang}
													defaultLanguage={defaultLanguage}
													aiTranslated={aiTranslated}
													onApprove={isEditing ? () => approveTranslation('presentation') : undefined}
												/>
											</div>
										</>
									)}
								</form.Subscribe>
								<form.AppField
									name="subdomain"
									children={(field) => (
										<field.SuffixTextField
											label={t`Portal URL`}
											suffix={getPortalDomainSuffix()}
											readOnlyHref={buildPortalUrl(portalId)}
											disabled
										/>
									)}
								/>
							</CardContent>
						</Card>

						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Images`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<div>
									<div className="mt-2">
										<form.AppField
											name="logo"
											children={(field) => (
												<field.ImageUploadField
													{...uploadTexts.imageUpload()}
													currentUrl={portal.logo?.location}
													label={t`Portal logo`}
													description={t`The entity's logo, which will appear in the upper-left corner of the portal.`}
													buttonLabel={t`Upload file`}
													contain
													size="compact"
												/>
											)}
										/>
									</div>
								</div>
								<div>
									<div className="mt-2">
										<form.AppField
											name="coverImage"
											children={(field) => (
												<field.ImageUploadField
													{...uploadTexts.imageUpload({ width: 630, height: 433 })}
													imageEditorTexts={uploadTexts.imageEditorTexts({ width: 630, height: 433 })}
													currentUrl={portal.media?.location}
													label={t`Cover image`}
													description={t`The portal's main image, which will be displayed in a prominent position.`}
													buttonLabel={t`Upload file`}
													enableCrop
													aspectRatio={{ width: 630, height: 433 }}
													size="compact"
												>
													<form.Subscribe
														selector={(state: {
															values: {
																aiTranslated: AiTranslatedMeta;
																defaultLanguage: string;
															};
														}) => ({
															aiTranslated: state.values.aiTranslated,
															defaultLanguage: state.values.defaultLanguage,
														})}
													>
														{({
															aiTranslated,
															defaultLanguage,
														}: {
															aiTranslated: AiTranslatedMeta;
															defaultLanguage: string;
														}) => (
															<div className="flex flex-col gap-2">
																<form.AppField
																	name={`imageAlt.${selectedLang}`}
																	listeners={{ onChange: () => approveTranslation('imageAlt') }}
																	children={(altField) => (
																		<altField.AltTextField
																			{...uploadTexts.altText}
																			placeholder={t`Describe the cover image`}
																		/>
																	)}
																/>
																<AiTranslatedHint
																	fieldName="imageAlt"
																	selectedLang={selectedLang}
																	defaultLanguage={defaultLanguage}
																	aiTranslated={aiTranslated}
																	onApprove={isEditing ? () => approveTranslation('imageAlt') : undefined}
																/>
															</div>
														)}
													</form.Subscribe>
												</field.ImageUploadField>
											)}
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Color configuration`}</CardTitle>
							</CardHeader>
							<CardContent className="flex gap-6">
								<form.AppField
									name="themeBaseColor"
									children={(field) => (
										<ColorField
											label={t`Primary color`}
											info={t`It will be used for the background of images and for the buttons.`}
											value={field.state.value}
											onChange={(v) => field.handleChange(v)}
											readOnly={!isEditing}
										/>
									)}
								/>
								<form.AppField
									name="themeSecondaryColor"
									children={(field) => (
										<ColorField
											label={t`Secondary color`}
											info={t`It will be used for the text.`}
											value={field.state.value}
											onChange={(v) => field.handleChange(v)}
											readOnly={!isEditing}
										/>
									)}
								/>
							</CardContent>
						</Card>
					</>
				)}

				{activeTab === 'funding' && (
					<form.Subscribe
						selector={(state) => ({
							donationAmounts: state.values.donationAmounts,
							aiTranslated: state.values.aiTranslated,
							defaultLanguage: state.values.defaultLanguage,
						})}
					>
						{({ donationAmounts, aiTranslated, defaultLanguage }) => (
							<DonationAmountsCard
								donationAmounts={donationAmounts}
								defaultLanguage={defaultLanguage}
								aiTranslated={aiTranslated}
								isEditing={isEditing}
								headerActions={languageDropdown}
								context="portal"
								renderAmountField={(index, label, readOnly) => (
									<form.AppField
										name={`donationAmounts[${index}].amount`}
										children={(field) => <field.CurrencyField label={label} className="w-30" readOnly={readOnly} />}
									/>
								)}
								renderEquivalentField={(index, lang, label, placeholder, readOnly) => (
									<form.AppField
										name={`donationAmounts[${index}].equivalent.${lang}`}
										children={(field) => (
											<field.TextField label={label} placeholder={placeholder} readOnly={readOnly} />
										)}
									/>
								)}
							/>
						)}
					</form.Subscribe>
				)}

				{activeTab === 'testimonials' && (
					<SliderCard
						form={form}
						isEditing={isEditing}
						selectedLang={selectedLang}
						languageDropdown={languageDropdown}
					/>
				)}

				{activeTab === 'faq' && (
					<FaqCard form={form} isEditing={isEditing} selectedLang={selectedLang} languageDropdown={languageDropdown} />
				)}

				{activeTab === 'languages' && (
					<Card className="shadow-warm">
						<CardHeader>
							<CardTitle className="text-xl">{t`Languages`}</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-2">
							<p className="text-muted-foreground text-sm">
								{t`Define the languages in which you want your portal to be available. The default language is the one you'll use to create the portal's content. We will automatically translate the details of your portal to the selected languages and you'll be able to review those translations later.`}
							</p>
							<form.Subscribe selector={(state) => state.values.defaultLanguage}>
								{(defaultLanguage) => (
									<form.AppField name="languages">
										{(field) => (
											<field.LanguagesPickerField
												defaultText={t`Default`}
												setAsDefaultText={t`Set as default`}
												defaultLanguage={defaultLanguage}
												onDefaultLanguageChange={(lang) => form.setFieldValue('defaultLanguage', lang)}
												onLanguageRemoved={(langCode) => {
													// Clean up all translatable Record<string, string> fields (title, presentation, imageAlt, donation equivalents)
													for (const { path, record } of collectTranslatableEntries(
														form.state.values as Record<string, unknown>,
													)) {
														if (record[langCode]) {
															const { [langCode]: _, ...rest } = record;
															form.setFieldValue(path as keyof typeof form.state.values, rest);
														}
													}
													// Delete from FAQ items
													const faqItems = form.getFieldValue('faqItems');
													if (faqItems[langCode]) {
														const { [langCode]: _, ...rest } = faqItems;
														form.setFieldValue('faqItems', rest);
													}
													// Delete from slider item texts
													const sliderItems = form.getFieldValue('sliderItems');
													for (let i = 0; i < sliderItems.length; i++) {
														const texts = sliderItems[i]!.texts;
														if (texts[langCode]) {
															const { [langCode]: _, ...rest } = texts;
															form.setFieldValue(`sliderItems[${i}].texts`, rest);
														}
													}
													// Remove language from aiTranslated tracking
													const ai = form.getFieldValue('aiTranslated');
													const updatedAi: Record<string, string[]> = {};
													for (const [field, langs] of Object.entries(ai)) {
														if (!Array.isArray(langs)) continue;
														const filtered = langs.filter((l) => l !== langCode);
														if (filtered.length > 0) updatedAi[field] = filtered;
													}
													form.setFieldValue('aiTranslated', updatedAi);
													// If the removed language was currently viewed, switch to default
													if (selectedLang === langCode) {
														const newDefault = form.getFieldValue('defaultLanguage') as string;
														navigate({
															search: (prev) => ({
																...prev,
																plang: prev.plang == null ? newDefault : undefined,
															}),
														});
													}
												}}
											/>
										)}
									</form.AppField>
								)}
							</form.Subscribe>
						</CardContent>
					</Card>
				)}
			</form>

			{isEditing && (
				<EditFooter
					form={form}
					formId="portal-edit-form"
					portalId="portal-footer-portal"
					onDiscard={() => navigate({ search: (prev) => ({ ...prev, editing: undefined }) })}
				/>
			)}
		</FormReadOnlyProvider>
	);
}

const SliderCard = withForm({
	...portalFormOpts,
	props: {} as { isEditing: boolean; selectedLang: string; languageDropdown?: React.ReactNode },
	render: function SliderCard({ form, isEditing, selectedLang, languageDropdown }) {
		const { t } = useLingui();
		const uploadTexts = useUploadTexts();
		const { newKeys, interacted, trackAdd, trackRemove } = useAnimatedList();

		const addSlide = () => {
			const key = crypto.randomUUID();
			trackAdd(key);
			const current = form.getFieldValue('sliderItems') ?? [];
			const languages = form.getFieldValue('languages') ?? [];
			const newTexts: Record<string, { quote: string; authorName: string; imageAlt: string }> = {};
			for (const lang of languages) {
				newTexts[lang] = { quote: '', authorName: '', imageAlt: '' };
			}
			form.setFieldValue('sliderItems', [...current, { _key: key, image: undefined, texts: newTexts }]);
		};

		const removeSlide = (index: number) => {
			trackRemove();
			const current = form.getFieldValue('sliderItems') ?? [];
			form.setFieldValue(
				'sliderItems',
				current.filter((_, i) => i !== index),
			);
		};

		return (
			<Card className="shadow-warm">
				<CardHeader className="flex items-center justify-between">
					<CardTitle className="text-xl">{t`Testimonials`}</CardTitle>
					{languageDropdown}
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					<p className="text-muted-foreground text-sm">
						{t`You can add images and texts with testimonials that reflect the impact of the project.`}
					</p>
					<form.Subscribe selector={(state) => state.values.sliderItems}>
						{(sliderItems) => (
							<div className="flex flex-col gap-2">
								{sliderItems.length === 0 && (
									<p className="text-muted-foreground text-sm">{t`No testimonials added yet.`}</p>
								)}
								<AnimatedList
									items={sliderItems}
									newKeys={newKeys}
									interacted={interacted}
									renderItem={(item, index) => (
										<div className="border-border flex flex-col gap-4 rounded-lg border p-4">
											<div className="flex h-4 items-center justify-between">
												<span className="text-sm font-medium">
													{t`Testimonial`} {index + 1}
												</span>
												{isEditing && (
													<Button type="button" variant="ghost" size="icon" onClick={() => removeSlide(index)}>
														<Trash2Icon className="size-4" />
													</Button>
												)}
											</div>

											<form.AppField
												name={`sliderItems[${index}].image`}
												children={(field) => (
													<field.ImageUploadField
														{...uploadTexts.imageUpload({ width: 1280, height: 840 })}
														imageEditorTexts={uploadTexts.imageEditorTexts({ width: 1280, height: 840 })}
														currentUrl={item.existingImageUrl}
														label={t`Testimonial image`}
														buttonLabel={t`Upload image`}
														enableCrop
														aspectRatio={{ width: 1280, height: 840 }}
														size="compact"
													>
														<form.AppField
															name={`sliderItems[${index}].texts.${selectedLang}.imageAlt`}
															children={(altField) => (
																<altField.AltTextField {...uploadTexts.altText} placeholder={t`Describe this image`} />
															)}
														/>
													</field.ImageUploadField>
												)}
											/>

											<form.AppField
												name={`sliderItems[${index}].texts.${selectedLang}.quote`}
												children={(field) => (
													<field.SimpleEditorField
														label={t`Quote`}
														placeholder={t`Example: To go to school I have to walk for 3 hours along muddy paths. Now, if we get the funding needed for this project, I will be able to study close to home.`}
													/>
												)}
											/>
											<form.AppField
												name={`sliderItems[${index}].texts.${selectedLang}.authorName`}
												children={(field) => (
													<field.TextField label={t`Author name`} placeholder={t`Example: Aminata, Senegal`} />
												)}
											/>
										</div>
									)}
									showAction={isEditing}
									action={
										<Button type="button" variant="outline-dark" onClick={addSlide} className="gap-1.5">
											<PlusIcon className="size-4" />
											{t`Add`}
										</Button>
									}
								/>
							</div>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>
		);
	},
});

const FaqCard = withForm({
	...portalFormOpts,
	props: {} as { isEditing: boolean; selectedLang: string; languageDropdown?: React.ReactNode },
	render: function FaqCard({ form, isEditing, selectedLang, languageDropdown }) {
		const { t } = useLingui();
		const { newKeys, interacted, trackAdd, trackRemove } = useAnimatedList();

		const addFaqItem = () => {
			const key = crypto.randomUUID();
			trackAdd(key);
			const current = form.getFieldValue(`faqItems.${selectedLang}`) ?? [];
			form.setFieldValue(`faqItems.${selectedLang}`, [...current, { _key: key, question: '', answer: '' }]);
		};

		const removeFaqItem = (index: number) => {
			trackRemove();
			const current = form.getFieldValue(`faqItems.${selectedLang}`) ?? [];
			form.setFieldValue(
				`faqItems.${selectedLang}`,
				current.filter((_, i) => i !== index),
			);
		};

		return (
			<Card className="shadow-warm">
				<CardHeader className="flex items-center justify-between">
					<CardTitle className="text-xl">{t`FAQ`}</CardTitle>
					{languageDropdown}
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					<p className="text-muted-foreground text-sm">
						{t`Here you can add a guide of frequently asked questions for the doubts that visitors to the portal might have. It is an optional section that can be useful for explaining where the funding goes, doubts about tax deductions on donations, etc.`}
					</p>
					<form.Subscribe selector={(state) => state.values.faqItems[selectedLang] ?? []}>
						{(faqItems) => (
							<div className="flex flex-col gap-2">
								{faqItems.length === 0 && <p className="text-muted-foreground text-sm">{t`No FAQ items added yet.`}</p>}
								<AnimatedList
									items={faqItems}
									newKeys={newKeys}
									interacted={interacted}
									renderItem={(item, index) => (
										<div className="border-border flex flex-col gap-3 rounded-lg border p-4">
											<div className="flex h-4 items-center justify-between">
												<span className="text-sm font-medium">
													{t`FAQ`} {index + 1}
												</span>
												{isEditing && (
													<Button type="button" variant="ghost" size="icon" onClick={() => removeFaqItem(index)}>
														<Trash2Icon className="size-4" />
													</Button>
												)}
											</div>
											<form.AppField
												name={`faqItems.${selectedLang}[${index}].question`}
												children={(field) => <field.TextField label={t`Question`} />}
											/>
											<form.AppField
												name={`faqItems.${selectedLang}[${index}].answer`}
												children={(field) => <field.SimpleEditorField label={t`Answer`} />}
											/>
										</div>
									)}
									showAction={isEditing}
									action={
										<Button type="button" variant="outline-dark" onClick={addFaqItem} className="gap-1.5">
											<PlusIcon className="size-4" />
											{t`Add`}
										</Button>
									}
								/>
							</div>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>
		);
	},
});

function ColorField(props: {
	label: string;
	info?: string;
	value: string;
	onChange: (v: string) => void;
	readOnly: boolean;
}) {
	return (
		<div className="flex-1">
			<label className="text-muted-foreground text-sm font-medium">{props.label}</label>
			{props.info && <p className="text-muted-foreground mt-1 text-xs">{props.info}</p>}
			<div className="mt-1.5 flex items-center gap-3">
				<div className="relative size-8 shrink-0 overflow-hidden rounded-lg">
					<div className="absolute inset-0 rounded-lg" style={{ backgroundColor: props.value }} />
					{!props.readOnly && (
						<input
							type="color"
							value={props.value}
							onChange={(e) => props.onChange(e.target.value)}
							className="absolute inset-0 cursor-pointer opacity-0"
						/>
					)}
				</div>
				<Input
					value={props.value}
					onChange={(e) => props.onChange(e.target.value)}
					readOnly={props.readOnly}
					tabIndex={props.readOnly ? -1 : undefined}
					placeholder="#000000"
					className={cn(
						'w-28',
						props.readOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
					)}
				/>
			</div>
		</div>
	);
}
